import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import PublicChatWidget from "@/components/PublicChatWidget";
import ROICalculator from "@/components/ROICalculator";
import CallExplainer from "@/components/CallExplainer";
import { ArrowUpRight, Check, Sparkles } from "lucide-react";
import {
  T, FONT_DISPLAY, FONT_MONO, PageShell, TopNav, Footer,
  container, eyebrow, h2, italicAccent, btnCoral, btnInk,
} from "@/lib/hybridTheme";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function IndustryLanding() {
  const { industry } = useParams();
  const [meta, setMeta] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, s] = await Promise.all([
          axios.get(`${BACKEND}/api/public/industries/${industry}`),
          axios.get(`${BACKEND}/api/public/site-config`),
        ]);
        setMeta(m.data);
        setAgent(s.data.agent || null);
      } catch { setNotFound(true); }
    })();
  }, [industry]);

  const roiDefaults = useMemo(() => meta?.roi_defaults || null, [meta]);
  if (notFound) return <Navigate to="/industries" replace />;

  return (
    <PageShell testId={`industry-page-${industry}`}>
      <TopNav activeKey="industries" />

      <section style={{ padding: "112px 0 72px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -100, right: -120, width: 700, height: 700, background: "radial-gradient(circle, rgba(229,83,59,0.08) 0%, transparent 60%)", pointerEvents: "none", filter: "blur(50px)" }} />
        <div style={container()}>
          <div style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 56, alignItems: "center", position: "relative" }}>
            <div style={{ maxWidth: 640 }}>
              <Link to="/industries" style={{ ...eyebrow(), textDecoration: "none", display: "inline-block", marginBottom: 12 }}>
                ← All industries
              </Link>
              <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(48px, 5.8vw, 84px)", lineHeight: 0.98, letterSpacing: "-0.035em", fontWeight: 400, margin: "8px 0 24px", color: T.ink }}>
                An AI receptionist built for<br />
                <em style={italicAccent(T.coral)}>{meta?.label || industry}</em>.
              </h1>
              <p style={{ fontSize: 20, lineHeight: 1.55, color: T.inkMuted, maxWidth: 560, margin: 0 }}>
                Every call answered, every appointment booked — automatically. Same tech, tuned for the specific calls your {(meta?.label || industry).toLowerCase()} business actually gets.
              </p>
              <div style={{ marginTop: 40, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <Link to="/#contact" style={{ ...btnCoral(), padding: "16px 26px", fontSize: 15, textDecoration: "none" }}>
                  Book a 15-min demo <ArrowUpRight size={17} />
                </Link>
                <a href="#calc" style={{ ...btnInk(), padding: "16px 26px", fontSize: 15, textDecoration: "none" }}>
                  Run the ROI calculator
                </a>
              </div>
            </div>
            <div style={{ justifySelf: "end", width: "100%", maxWidth: 460 }}>
              <CallExplainer
                industryLabel={meta?.label || industry}
                transcript={meta?.sample_call || []}
                businessName={`${meta?.label || industry} pros · demo`}
              />
            </div>
          </div>
        </div>
      </section>

      {meta?.context && (
        <section style={{ padding: "72px 0", background: T.canvas, borderTop: `1px solid ${T.lineSoft}`, borderBottom: `1px solid ${T.lineSoft}` }}>
          <div style={container()}>
            <div style={{ maxWidth: 820 }}>
              <div style={eyebrow()}><Sparkles size={12} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} /> Industry context</div>
              <p style={{ marginTop: 16, fontSize: 18, lineHeight: 1.7, color: T.ink, whiteSpace: "pre-line" }}>{meta.context}</p>
            </div>
          </div>
        </section>
      )}

      <section id="calc" style={{ padding: "96px 0", background: T.panel, borderBottom: `1px solid ${T.lineSoft}` }}>
        <div style={container()}>
          <div style={{ maxWidth: 720, marginBottom: 40 }}>
            <div style={eyebrow()}>Your numbers</div>
            <h2 style={h2()}>What are missed calls <em style={italicAccent(T.coral)}>actually</em> costing you?</h2>
          </div>
          {roiDefaults && (
            <div style={{ background: T.cream, color: T.ink, padding: "40px 44px", border: `1px solid ${T.line}` }}>
              <ROICalculator defaults={roiDefaults} industryKey={industry} industryLabel={meta?.label || ""} showEyebrow={false} />
            </div>
          )}
        </div>
      </section>

      <section style={{ padding: "96px 0" }}>
        <div style={container()}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 1, background: T.line }}>
            {[
              { n: "I.",   t: "Free preview site",    d: `Built for your ${(meta?.label || "").toLowerCase()} business in 24 hours.` },
              { n: "II.",  t: "AI agent tuned live",   d: "Trained on your services, hours, service area, and pricing." },
              { n: "III.", t: "Auto-follow-up runs",   d: "Missed calls get texted back automatically. Cold leads get nurtured." },
            ].map((s) => (
              <div key={s.n} style={{ background: T.panel, padding: "40px 32px" }}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 52, color: T.coral, fontStyle: "italic", fontWeight: 400, lineHeight: 1 }}>{s.n}</div>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 500, marginTop: 24, color: T.ink }}>{s.t}</div>
                <p style={{ marginTop: 12, color: T.inkMuted, fontSize: 15, lineHeight: 1.65 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: "72px 0 120px" }}>
        <div style={container()}>
          <div style={{ background: T.ink, color: T.cream, padding: "56px 48px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, background: `radial-gradient(circle, ${T.coral} 0%, transparent 65%)`, opacity: 0.55, borderRadius: "50%" }} />
            <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 40, alignItems: "center" }}>
              <div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 52, fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1, margin: 0 }}>
                  Ready for a<br /><em style={italicAccent(T.coral)}>{meta?.label || industry}</em> preview?
                </h3>
                <p style={{ marginTop: 20, maxWidth: 460, lineHeight: 1.6, opacity: 0.75 }}>
                  15-min call. Free preview site delivered inside 24 hours. Zero commitment.
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
                <Link to="/#contact" style={{ background: T.coral, color: T.cream, border: "none", padding: "18px 28px", fontSize: 15, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, minWidth: 240, justifyContent: "center" }}>
                  Book my demo <ArrowUpRight size={16} />
                </Link>
                <Link to="/pricing-public" style={{ background: "transparent", color: T.cream, border: `1px solid rgba(247,244,238,0.3)`, padding: "18px 28px", fontSize: 15, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, minWidth: 240, justifyContent: "center" }}>
                  See pricing <Check size={15} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <PublicChatWidget agent={agent} />
    </PageShell>
  );
}
